import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FlagIcon } from '@heroicons/react/24/outline'
import moment from 'moment'

const GHOST_MICRO_VOLUME_ID = 'GHOST_MICRO_VOLUME'

export default function ProgramLengthTimelineDisplay({
    trainingTimelinePrograms = [],
    kinesisEvents = [],
    allowProgramCanvas = false,
    renderRangeStart,
    renderRangeEnd,
    daySpacing = 0,
    DateToTrackLeftX = () => { return 0 }
}) {
    const navigate = useNavigate()

    const OpenProgramCanvas = (programId) => {
        if (allowProgramCanvas) {
            navigate(`/program/${programId}/canvas`)
        }
    }

    const displayData = useMemo(() => {
        if (daySpacing <= 0 || !Array.isArray(trainingTimelinePrograms) || trainingTimelinePrograms.length === 0) {
            return []
        }

        return trainingTimelinePrograms.flatMap((trainingTimelineProgram) => {
            const { name, startDate: startDateRaw, endDate: endDateRaw } = trainingTimelineProgram ?? {}

            if (!name || !startDateRaw || !endDateRaw) {
                return []
            }

            const startDate = moment(startDateRaw).startOf('day')
            const endDate = moment(endDateRaw).startOf('day')

            if (
                !startDate.isValid()
                || !endDate.isValid()
                || endDate.isBefore(renderRangeStart, 'day')
                || startDate.isAfter(renderRangeEnd, 'day')
            ) {
                return []
            }

            const left = DateToTrackLeftX(startDate)
            const width = DateToTrackLeftX(endDate.clone().add(1, 'day')) - left

            return [{
                left,
                width,
                programId: trainingTimelineProgram?.programId,
                name,
                eventFlags: GetMicroVolumeEventFlags(trainingTimelineProgram, kinesisEvents, renderRangeStart, renderRangeEnd, DateToTrackLeftX),
            }]
        })
    }, [
        trainingTimelinePrograms,
        kinesisEvents,
        renderRangeStart,
        renderRangeEnd,
        daySpacing,
        DateToTrackLeftX,
    ])

    if (displayData.length === 0) {
        return <div className='relative h-4 2xl:h-5' />
    }

    return (
        <div className='relative h-4 2xl:h-5'>
            {displayData.map((bar, index) => (
                <div
                    key={index}
                    className={`
                        absolute bottom-0 h-4 2xl:h-5 px-[1.5px] 2xl:px-[2px] transition-opacity 
                        ${allowProgramCanvas ? 'cursor-pointer' : ''}
                    `}
                    style={{
                        left: `${bar.left}px`,
                        width: `${bar.width}px`,
                    }}
                    onClick={() => OpenProgramCanvas(bar?.programId)}
                >
                    <div className='h-full flex items-center justify-center overflow-hidden rounded border border-gray-400'>
                        {/* Rendered above the flags; a flag under the centered name is an accepted edge case. */}
                        <span
                            className='relative z-20 text-xxs 2xl:text-xs font-medium whitespace-nowrap px-1 bg-white rounded-sm'
                            title={bar.name}
                        >
                            {bar.name}
                        </span>
                    </div>
                </div>
            ))}

            {displayData.flatMap((bar) => bar.eventFlags).map((eventFlag) => (
                <div
                    key={eventFlag.key}
                    className='absolute inset-y-0 z-10 flex items-center justify-center pointer-events-none'
                    style={{
                        left: `${eventFlag.left}px`,
                        width: `${eventFlag.width}px`,
                    }}
                >
                    <FlagIcon className='w-3 h-3 2xl:w-4 2xl:h-4 text-gray-700' data-testid='program-length-event-flag' />
                </div>
            ))}
        </div>
    )
}

// One flag per micro volume week of the program that contains at least one event.
function GetMicroVolumeEventFlags(trainingTimelineProgram, kinesisEvents, renderRangeStart, renderRangeEnd, DateToTrackLeftX) {
    const eventDates = (kinesisEvents ?? [])
        .map((event) => moment(event?.eventDate).startOf('day'))
        .filter((eventDate) => eventDate.isValid())

    if (eventDates.length === 0) {
        return []
    }

    const eventFlags = []

    for (const mesoVolume of trainingTimelineProgram?.compiledProgramVolume?.mesoVolumes ?? []) {
        for (const microVolumeConfig of mesoVolume.microVolumes ?? []) {
            if (microVolumeConfig.microVolumeId === GHOST_MICRO_VOLUME_ID) continue

            const startDate = moment(microVolumeConfig.startDate).startOf('day')
            const endDate = moment(microVolumeConfig.endDate).startOf('day')

            if (
                !startDate.isValid()
                || !endDate.isValid()
                || endDate.isBefore(renderRangeStart, 'day')
                || startDate.isAfter(renderRangeEnd, 'day')
                || !eventDates.some((eventDate) => eventDate.isBetween(startDate, endDate, 'day', '[]'))
            ) {
                continue
            }

            const left = DateToTrackLeftX(startDate)
            const width = DateToTrackLeftX(endDate.clone().add(1, 'day')) - left

            eventFlags.push({
                key: `${trainingTimelineProgram?.programId}-${microVolumeConfig.weekOfTheYearKey ?? startDate.format('YYYY-MM-DD')}`,
                left,
                width,
            })
        }
    }

    return eventFlags
}
